import { useCallback, useEffect, useState } from "react";
import { UserButton } from "@clerk/react";
import { Camera, Heart, History, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import ScanTab from "@/components/app/ScanTab";
import FavoritesTab from "@/components/app/FavoritesTab";
import HistoryTab from "@/components/app/HistoryTab";
import ShoppingTab from "@/components/app/ShoppingTab";
import RecipeDetail from "@/components/app/RecipeDetail";

const TABS = [
  { key: "scan", label: "Scansiona", icon: Camera, testid: "nav-tab-camera" },
  { key: "favorites", label: "Preferiti", icon: Heart, testid: "nav-tab-favorites" },
  { key: "history", label: "Cronologia", icon: History, testid: "nav-tab-history" },
  { key: "shopping", label: "Spesa", icon: ShoppingBasket, testid: "nav-tab-shopping-list" },
];

export default function Dashboard() {
  const api = useApi();
  const [tab, setTab] = useState("scan");
  const [favorites, setFavorites] = useState([]);
  const [shopping, setShopping] = useState([]);
  const [openRecipe, setOpenRecipe] = useState(null);
  const [currentScan, setCurrentScan] = useState(null);

  const loadFavs = useCallback(() => api("get", "/favorites").then(setFavorites).catch(() => {}), [api]);
  const loadShopping = useCallback(() => api("get", "/shopping").then(setShopping).catch(() => {}), [api]);
  useEffect(() => { loadFavs(); loadShopping(); }, [loadFavs, loadShopping]);

  const isFav = (r) => favorites.some((f) => f.recipe.id === r.id);
  const toggleFav = async (r) => {
    try {
      if (isFav(r)) {
        await api("delete", `/favorites/${r.id}`);
        toast("Rimossa dai preferiti");
      } else {
        await api("post", "/favorites", { recipe: r });
        toast.success("Salvata nei preferiti");
      }
      loadFavs();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addToShopping = async (items) => {
    if (!items.length) return;
    try {
      const { added } = await api("post", "/shopping", { items });
      toast.success(added ? `${added} ingredient${added > 1 ? "i aggiunti" : "e aggiunto"} alla lista spesa` : "Già presenti nella lista spesa");
      loadShopping();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openScan = (scan) => { setCurrentScan(scan); setTab("scan"); };
  const pending = shopping.filter((s) => !s.checked).length;

  return (
    <div className="relative z-10 min-h-screen pb-24 md:pb-10" data-testid="dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FDFBF7]/85 border-b border-[#1C241E]/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-8 py-3">
          <span data-testid="header-logo" className="font-display text-xl sm:text-2xl font-extrabold text-[#2E6F40]">
            Svuota<span className="text-[#D95338]">Frigo</span>
          </span>
          <nav className="hidden md:flex gap-1">
            {TABS.map((t) => (
              <button key={t.key} data-testid={`${t.testid}-desktop`} onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-[#2E6F40] text-white" : "text-[#4F5D52] hover:bg-[#E8F3EA]"}`}>
                <t.icon className="h-4 w-4" />{t.label}
                {t.key === "shopping" && pending > 0 && <span className="font-mono text-[10px] rounded-full bg-[#D95338] text-white px-1.5">{pending}</span>}
              </button>
            ))}
          </nav>
          <div data-testid="user-menu"><UserButton /></div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10">
        {tab === "scan" && <ScanTab initialScan={currentScan} onOpen={setOpenRecipe} isFav={isFav} toggleFav={toggleFav} />}
        {tab === "favorites" && <FavoritesTab favorites={favorites} onOpen={setOpenRecipe} isFav={isFav} toggleFav={toggleFav} />}
        {tab === "history" && <HistoryTab onOpenScan={openScan} />}
        {tab === "shopping" && <ShoppingTab items={shopping} reload={loadShopping} />}
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-xl border-t border-[#1C241E]/10 grid grid-cols-4">
        {TABS.map((t) => (
          <button key={t.key} data-testid={t.testid} onClick={() => setTab(t.key)}
            className={`relative flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${tab === t.key ? "text-[#2E6F40]" : "text-[#4F5D52]/70"}`}>
            <t.icon className="h-5 w-5" />{t.label}
            {t.key === "shopping" && pending > 0 && <span className="absolute top-1.5 right-[28%] font-mono text-[9px] rounded-full bg-[#D95338] text-white px-1">{pending}</span>}
          </button>
        ))}
      </nav>

      <RecipeDetail recipe={openRecipe} onClose={() => setOpenRecipe(null)} isFav={isFav} toggleFav={toggleFav} addToShopping={addToShopping} />
    </div>
  );
}
